const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('access_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
  }

  ensureToken() {
    // Ensure token is loaded from localStorage
    if (!this.token) {
      this.token = localStorage.getItem('access_token');
    }
    return this.token;
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    // Get token fresh from localStorage each time
    const token = localStorage.getItem('access_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async request(endpoint, options = {}) {
    // Ensure token is loaded before making request
    this.ensureToken();
    
    const url = `${API_URL}${endpoint}`;
    const config = {
      ...options,
      headers: this.getHeaders(),
    };

    try {
      const response = await fetch(url, config);
      
      // Clone response immediately before any processing to avoid "body already used" errors
      // This is needed because monitoring scripts (like rrweb) may also try to clone the response
      let responseClone;
      try {
        responseClone = response.clone();
      } catch (cloneError) {
        // If cloning fails, continue with original response
        console.warn('Response clone failed, continuing with original:', cloneError);
        responseClone = response;
      }
      
      // Check for 401 unauthorized - redirect to login
      if (responseClone.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }
      
      // Check response before reading body
      if (!responseClone.ok) {
        let errorMessage = `Request failed with status ${responseClone.status}`;
        try {
          const errorData = await responseClone.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // If JSON parsing fails, use status text or default message
          errorMessage = responseClone.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const data = await responseClone.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Auth
  async register(email, name, password) {
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, name, password }),
    });
    if (data.access_token) {
      this.setToken(data.access_token);
    }
    return data;
  }

  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (data.access_token) {
      this.setToken(data.access_token);
    }
    return data;
  }

  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  async logout() {
    await this.request('/api/auth/logout', { method: 'POST' });
    this.setToken(null);
  }

  async refreshToken() {
    try {
      const data = await this.request('/api/auth/refresh', {
        method: 'POST'
      });
      if (data.access_token) {
        this.setToken(data.access_token);
        console.log('Token refreshed successfully');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // Auto-refresh token setup
  setupAutoRefresh() {
    // Refresh token every 6 hours (before 8-hour expiration)
    const refreshInterval = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
    
    setInterval(async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        console.log('Auto-refreshing token...');
        await this.refreshToken();
      }
    }, refreshInterval);

    // Also refresh on user activity (debounced)
    let activityTimeout = null;
    const refreshOnActivity = () => {
      if (activityTimeout) clearTimeout(activityTimeout);
      
      activityTimeout = setTimeout(async () => {
        const token = localStorage.getItem('access_token');
        const lastRefresh = localStorage.getItem('last_token_refresh');
        const now = Date.now();
        
        // Only refresh if it's been more than 30 minutes since last refresh
        if (token && (!lastRefresh || now - parseInt(lastRefresh) > 30 * 60 * 1000)) {
          console.log('Activity-based token refresh...');
          const success = await this.refreshToken();
          if (success) {
            localStorage.setItem('last_token_refresh', now.toString());
          }
        }
      }, 5000); // Debounce for 5 seconds
    };

    // Listen for user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, refreshOnActivity, { passive: true });
    });

    // Re-sync token when page becomes visible (handles tab switching)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Page is now visible - ensure token is fresh
        this.ensureToken();
      }
    });

    // Listen for localStorage changes from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === 'access_token') {
        // Token changed in another tab - sync it
        this.token = e.newValue;
      }
    });
  }

  async updateProfile(profileData) {
    return this.request('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // Children
  async getChildren() {
    return this.request('/api/children');
  }

  async createChild(childData) {
    return this.request('/api/children', {
      method: 'POST',
      body: JSON.stringify(childData),
    });
  }

  async updateChild(childId, childData) {
    return this.request(`/api/children/${childId}`, {
      method: 'PUT',
      body: JSON.stringify(childData),
    });
  }

  async updateChildPin(childId, pin) {
    return this.request(`/api/children/${childId}/pin`, {
      method: 'PUT',
      body: JSON.stringify({ pin }),
    });
  }

  async updateChildActiveStatus(childId, active) {
    return this.request(`/api/children/${childId}/active`, {
      method: 'PUT',
      body: JSON.stringify({ active }),
    });
  }

  async updateChildTokens(childId, tokens) {
    return this.request(`/api/children/${childId}/tokens`, {
      method: 'PUT',
      body: JSON.stringify({ tokens }),
    });
  }


  async deleteChild(childId) {
    return this.request(`/api/children/${childId}`, {
      method: 'DELETE',
    });
  }

  // Assignments
  async getAssignments(childId = null, status = null) {
    let url = '/api/assignments?';
    if (childId) url += `child_id=${childId}&`;
    if (status) url += `status=${status}`;
    return this.request(url);
  }

  async createAssignment(assignmentData) {
    return this.request('/api/assignments', {
      method: 'POST',
      body: JSON.stringify(assignmentData),
    });
  }

  async submitAssignment(assignmentId, submissionData) {
    return this.request(`/api/assignments/${assignmentId}/submit`, {
      method: 'POST',
      body: JSON.stringify(submissionData),
    });
  }

  async gradeAssignment(assignmentId, gradeData) {
    return this.request(`/api/assignments/${assignmentId}/grade`, {
      method: 'POST',
      body: JSON.stringify(gradeData),
    });
  }

  async updateGrade(assignmentId, gradeData) {
    return this.request(`/api/assignments/${assignmentId}/grade`, {
      method: 'PUT',
      body: JSON.stringify(gradeData),
    });
  }

  async sendBackAssignment(assignmentId, notes = '') {
    return this.request(`/api/assignments/${assignmentId}/send-back`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async getPendingAssignments() {
    return this.request('/api/assignments/pending');
  }

  async submitBonusWork(workData) {
    return this.request('/api/bonus-work', {
      method: 'POST',
      body: JSON.stringify(workData),
    });
  }

  // Lesson Plans
  async getLessonPlans(childId = null, archivedOnly = false) {
    const params = [];
    if (childId) params.push(`child_id=${childId}`);
    if (archivedOnly) params.push('archived_only=true');
    let url = '/api/lesson-plans';
    if (params.length) url += `?${params.join('&')}`;
    return this.request(url);
  }

  async createLessonPlan(planData) {
    return this.request('/api/lesson-plans', {
      method: 'POST',
      body: JSON.stringify(planData),
    });
  }

  async updateLessonPlan(planId, planData) {
    return this.request(`/api/lesson-plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(planData),
    });
  }

  async deleteLessonPlan(planId) {
    return this.request(`/api/lesson-plans/${planId}`, {
      method: 'DELETE',
    });
  }

  async duplicateLessonPlan(planId, childIds) {
    return this.request(`/api/lesson-plans/${planId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ child_ids: childIds }),
    });
  }

  // Goals
  async getGoals(childId = null) {
    let url = '/api/goals';
    if (childId) url += `?child_id=${childId}`;
    return this.request(url);
  }

  async createGoal(goalData) {
    return this.request('/api/goals', {
      method: 'POST',
      body: JSON.stringify(goalData),
    });
  }

  async updateGoal(goalId, goalData) {
    return this.request(`/api/goals/${goalId}`, {
      method: 'PUT',
      body: JSON.stringify(goalData),
    });
  }

  async deleteGoal(goalId) {
    return this.request(`/api/goals/${goalId}`, {
      method: 'DELETE',
    });
  }

  async deleteAssignment(assignmentId) {
    return this.request(`/api/assignments/${assignmentId}`, {
      method: 'DELETE',
    });
  }

  async updateAssignment(assignmentId, assignmentData) {
    return this.request(`/api/assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify(assignmentData)
    });
  }

  async moveAssignmentToPlan(assignmentId, newLessonPlanId) {
    return this.request(`/api/assignments/${assignmentId}/move-to-plan`, {
      method: 'PUT',
      body: JSON.stringify({ new_lesson_plan_id: newLessonPlanId })
    });
  }

  async addLessonPlanAttachment(planId, attachment) {
    return this.request(`/api/lesson-plans/${planId}/attachments`, {
      method: 'POST',
      body: JSON.stringify(attachment)
    });
  }

  async deleteLessonPlanAttachment(planId, attachmentIndex) {
    return this.request(`/api/lesson-plans/${planId}/attachments/${attachmentIndex}`, {
      method: 'DELETE'
    });
  }

  async addAssignmentAttachment(assignmentId, attachment) {
    return this.request(`/api/assignments/${assignmentId}/attachments`, {
      method: 'POST',
      body: JSON.stringify(attachment)
    });
  }

  async deleteAssignmentAttachment(assignmentId, attachmentIndex) {
    return this.request(`/api/assignments/${assignmentId}/attachments/${attachmentIndex}`, {
      method: 'DELETE'
    });
  }

  async updateAssignmentChecklist(assignmentId, checklist) {
    return this.request(`/api/assignments/${assignmentId}/checklist`, {
      method: 'PUT',
      body: JSON.stringify({ checklist })
    });
  }

  // Subjects
  async getSubjects() {
    return this.request('/api/subjects');
  }

  // Academic Standards (parent read)
  async getStandards(state = null) {
    let url = '/api/standards';
    if (state) url += `?state=${state}`;
    return this.request(url);
  }

  async getStandardsCoverage(childId, start = null, end = null) {
    let url = `/api/reports/standards-coverage?child_id=${childId}`;
    if (start) url += `&start=${encodeURIComponent(start)}`;
    if (end) url += `&end=${encodeURIComponent(end)}`;
    return this.request(url);
  }


  async createSubject(subjectData) {
    return this.request('/api/subjects', {
      method: 'POST',
      body: JSON.stringify(subjectData)
    });
  }

  async updateSubject(subjectId, subjectData) {
    return this.request(`/api/subjects/${subjectId}`, {
      method: 'PUT',
      body: JSON.stringify(subjectData)
    });
  }

  async deleteSubject(subjectId) {
    return this.request(`/api/subjects/${subjectId}`, {
      method: 'DELETE'
    });
  }

  // File upload method
  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const url = `${API_URL}/api/upload`;
    const config = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        // Don't set Content-Type - browser will set it with boundary for multipart/form-data
      },
      body: formData
    };

    try {
      const response = await fetch(url, config);
      
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }
      
      if (!response.ok) {
        let errorMessage = `Request failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  }

  // Payment/Subscription methods
  async getSubscriptionPlans() {
    return this.request('/api/payments/plans');
  }

  async createCheckoutSession(planId) {
    const origin_url = window.location.origin;
    return this.request('/api/payments/checkout/session', {
      method: 'POST',
      body: JSON.stringify({
        plan_id: planId,
        origin_url: origin_url
      })
    });
  }

  async getCheckoutStatus(sessionId) {
    return this.request(`/api/payments/checkout/status/${sessionId}`);
  }


  // Completed Work
  async getCompletedWork(childId = null, subjectId = null, startDate = null, endDate = null) {
    let url = '/api/assignments/completed?';
    if (childId) url += `child_id=${childId}&`;
    if (subjectId) url += `subject_id=${subjectId}&`;
    
    // Default to last 365 days if no date range specified
    if (!startDate && !endDate) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 365);
      startDate = start.toISOString().split('T')[0];
      endDate = end.toISOString().split('T')[0];
    }
    
    if (startDate) url += `start_date=${startDate}&`;
    if (endDate) url += `end_date=${endDate}&`;
    
    return this.request(url);
  }

  // Child Authentication
  async childLogin(username, pin) {
    return this.request('/api/auth/child-login', {
      method: 'POST',
      body: JSON.stringify({ username, pin }),
    }, false); // false = no auth token needed for login
  }

  // Reward Store
  async getRewardItems(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.active !== undefined) queryParams.append('active', params.active);
    if (params.month) queryParams.append('month', params.month);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    
    const queryString = queryParams.toString();
    return this.request(`/api/rewards/items${queryString ? `?${queryString}` : ''}`);
  }

  async getRewardPurchases(childId = null) {
    let url = '/api/rewards/purchases';
    if (childId) url += `?child_id=${childId}`;
    return this.request(url);
  }

  async purchaseReward(rewardId, childId) {
    return this.request(`/api/rewards/purchase?reward_id=${rewardId}&child_id=${childId}`, {
      method: 'POST',
    });
  }

}

const api = new ApiService();
export default api;
